import { useEffect, useMemo, useState } from 'react';

import {
  ApolloClient,
  ApolloProvider as ApolloProviderAlias,
  split,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { setContext } from '@apollo/client/link/context';

import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import useCookie from '../components/_hook/useCookie';
import useNotification from '../components/_hook/useNotification';
import { COOKIE_KEY } from '../components/constant/cookie';
import useEnvironment from '../components/_hook/useEnvironment';
import ApolloCache from './apolloCache';

const client = new ApolloClient({
  cache: ApolloCache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  },
});

export default function ApolloProvider(props: any) {
  const { cookies } = useCookie([
    COOKIE_KEY.API_TOKEN,
    COOKIE_KEY.LANGUAGE,
    COOKIE_KEY.COUNTRY,
    COOKIE_KEY.CURRENCY,
  ]);
  const [isReady, setIsReady] = useState<boolean>(false);
  const env = useEnvironment();
  const { notification } = useNotification();
  const apiToken = cookies[COOKIE_KEY.API_TOKEN];
  const language = cookies[COOKIE_KEY.LANGUAGE];
  const currency = cookies[COOKIE_KEY.CURRENCY];
  const country = cookies[COOKIE_KEY.COUNTRY];
  const httpLink = createUploadLink({
    headers: { 'Apollo-Require-Preflight': 'true' },
    uri: env.REACT_APP_API_URL,
  });
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      country,
      'accept-language': language,
      authorization: apiToken ? `Bearer ${apiToken}` : '',
    },
  }));

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        if (extensions && extensions.invalidArgs) {
          Object.keys(extensions.invalidArgs).forEach((key) => {
            const validationRules = extensions.invalidArgs[key];

            validationRules.forEach(
              (validationRule: { validationRule: string; message: string }) => {
                notification.error(`[${key}]: ${validationRule.message}`);
              }
            );
          });
        }
      });
    }

    if (networkError) {
      notification.error(`[Network error]: ${networkError}`);
    }
  });
  const splitLink = useMemo(() => {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: env.REACT_APP_API_WEBSOCKET_URL,
        connectionParams: {
          authorization: apiToken ? `Bearer ${apiToken}` : '',
        },
      })
    );

    return split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      errorLink.concat(authLink.concat(httpLink))
    );
  }, [apiToken, country, language, currency]);
  useEffect(() => {
    client.setLink(splitLink);
    setIsReady(true);
  }, [splitLink]);
  const { children } = props;

  if (!isReady) {
    return null;
  }

  return <ApolloProviderAlias client={client}>{children}</ApolloProviderAlias>;
}
